import {
  ALLOWED_DOCUMENT_EXTENSIONS,
  ALLOWED_NATIVE_EXTENSIONS,
  ALLOWED_PDF_EXTENSIONS,
  DOCUMENT_INDICES,
  MAX_NATIVE_SIZE_BYTES,
  MAX_PDF_SIZE_BYTES,
} from "@etudescarre/constants";
import { Button, Select } from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import { todayLocal } from "@/lib/utils/date";
import { useYardContext } from "@/providers/yard-context";
import {
  multipartSerializer,
  pickFile,
} from "../dossier-marche/dossier-marche-panels";

const inputClass =
  "w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100";
const labelClass =
  "mb-1 block text-sm font-medium text-zinc-900 dark:text-zinc-100";
const fileInputClass =
  "w-full text-sm text-zinc-500 file:mr-4 file:rounded-md file:border-0 file:bg-blue-50 file:px-4 file:py-2 file:text-sm file:font-medium file:text-blue-700 hover:file:bg-blue-100 dark:text-zinc-400 dark:file:bg-blue-900/20 dark:file:text-blue-400";

type CreatedFtm = { id: number; number: string };

type FtmFormData = {
  number: string;
  object: string;
  dateReception: string;
  dateRedaction: string;
};

function FtmForm({
  onCreated,
  onCancel,
}: {
  onCreated: (ftm: CreatedFtm) => void;
  onCancel: () => void;
}) {
  const { yard } = useYardContext();
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const form = useForm<FtmFormData>({
    defaultValues: {
      number: "",
      object: "",
      dateReception: todayLocal(),
      dateRedaction: "",
    },
  });

  const onSubmit = form.handleSubmit(async (data) => {
    setSubmitting(true);
    try {
      const metadata = {
        number: data.number,
        object: data.object,
        dateReception: new Date(data.dateReception).toISOString(),
        ...(data.dateRedaction
          ? { dateRedaction: new Date(data.dateRedaction).toISOString() }
          : {}),
      };
      const { data: created, error } = await apiClient.POST(
        "/api/yards/{yardId}/ftms",
        {
          params: { path: { yardId: String(yard.id) } },
          body: { metadata: JSON.stringify(metadata), file: file ?? undefined },
          bodySerializer: multipartSerializer,
        },
      );
      if (error || isNil(created)) {
        toaster.error({
          title: "Erreur",
          description: error?.error ?? "Échec de l'enregistrement de la FTM",
        });
        return;
      }
      toaster.success({ title: "Succès", description: "FTM enregistrée" });
      onCreated({ id: created.data.ftmId, number: data.number });
    } catch (err) {
      console.error("FTM registration failed", err);
      toaster.error({
        title: "Erreur",
        description: "Une erreur est survenue",
      });
    } finally {
      setSubmitting(false);
    }
  });

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
        Enregistrer la réception d'une FTM
      </h3>

      <div>
        <label htmlFor="ftmNumber" className={labelClass}>
          N° de la FTM <span className="text-red-500">*</span>
        </label>
        <input
          id="ftmNumber"
          type="text"
          {...form.register("number")}
          required
          maxLength={255}
          className={inputClass}
        />
      </div>

      <div>
        <label htmlFor="ftmObject" className={labelClass}>
          Objet <span className="text-red-500">*</span>
        </label>
        <input
          id="ftmObject"
          type="text"
          {...form.register("object")}
          required
          maxLength={512}
          className={inputClass}
        />
      </div>

      <div>
        <label htmlFor="ftmDateReception" className={labelClass}>
          Date de réception <span className="text-red-500">*</span>
        </label>
        <input
          id="ftmDateReception"
          type="date"
          {...form.register("dateReception")}
          required
          className={inputClass}
        />
      </div>

      <div>
        <label htmlFor="ftmDateRedaction" className={labelClass}>
          Date de rédaction
        </label>
        <input
          id="ftmDateRedaction"
          type="date"
          {...form.register("dateRedaction")}
          className={inputClass}
        />
      </div>

      <div>
        <label htmlFor="ftmFile" className={labelClass}>
          Fichier de la FTM
        </label>
        <input
          id="ftmFile"
          type="file"
          accept={ALLOWED_DOCUMENT_EXTENSIONS.map((e) => `.${e}`).join(",")}
          onChange={pickFile(
            setFile,
            ALLOWED_DOCUMENT_EXTENSIONS,
            MAX_PDF_SIZE_BYTES,
            "50 Mo",
          )}
          className={fileInputClass}
        />
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" outline onClick={onCancel} disabled={submitting}>
          Annuler
        </Button>
        <Button type="submit" disabled={submitting}>
          {submitting ? "En cours..." : "Enregistrer la FTM"}
        </Button>
      </div>
    </form>
  );
}

const IFC_EXTENSION = "ifc";
const NO_MAQUETTE = "0";

type FtmItemKind = "document" | "maquette";

type FtmDocumentFormData = {
  kind: FtmItemKind;
  authorContractId: string;
  number: string;
  title: string;
  indice: string;
  maquetteId: string;
};

type RegisteredFtmItem = {
  id: number;
  kind: FtmItemKind;
  number: string;
  title: string;
  indice: string;
  maquetteNumber: string | null;
};

const defaultDocumentValues: FtmDocumentFormData = {
  kind: "document",
  authorContractId: "",
  number: "",
  title: "",
  indice: "",
  maquetteId: NO_MAQUETTE,
};

/** Every active contract of the yard: the author may be any participant. */
function useYardAuthorOptions(yardId: number) {
  const [options, setOptions] = useState<{ label: string; value: string }[]>(
    [],
  );

  useEffect(() => {
    let cancelled = false;
    async function fetchContracts() {
      try {
        const { data, error } = await apiClient.GET(
          "/api/yards/{yardId}/contracts",
          { params: { path: { yardId: String(yardId) } } },
        );
        if (cancelled) return;
        if (error || isNil(data)) {
          toaster.error({
            title: "Erreur",
            description: "Impossible de charger les intervenants du chantier",
          });
          return;
        }
        setOptions(
          data.data.contracts
            .filter((c) => c.isActive)
            .map((c) => {
              const company = c.performerCompanyName ?? c.type;
              return {
                value: String(c.id),
                label: c.code ? `${company} (${c.code})` : company,
              };
            })
            .sort((a, b) => a.label.localeCompare(b.label)),
        );
      } catch (err) {
        console.error("Failed to load yard contracts", err);
      }
    }
    fetchContracts();
    return () => {
      cancelled = true;
    };
  }, [yardId]);

  return options;
}

function FtmDocumentsForm({
  ftm,
  onDone,
}: {
  ftm: CreatedFtm;
  onDone: () => void;
}) {
  const { yard } = useYardContext();
  const authorOptions = useYardAuthorOptions(yard.id);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [nativeFile, setNativeFile] = useState<File | null>(null);
  const [ifcFile, setIfcFile] = useState<File | null>(null);
  const [items, setItems] = useState<RegisteredFtmItem[]>([]);
  const [submitting, setSubmitting] = useState(false);
  // Remounting the form is the only way to clear the uncontrolled file inputs.
  const [formKey, setFormKey] = useState(0);
  const form = useForm<FtmDocumentFormData>({
    defaultValues: defaultDocumentValues,
  });
  // The PDF and IFC inputs unmount when the type changes: drop their files so
  // a file no longer shown is never sent.
  const kindField = form.register("kind", {
    onChange: () => {
      setPdfFile(null);
      setIfcFile(null);
    },
  });
  const kind = form.watch("kind");
  const authorContractId = form.watch("authorContractId");
  const indice = form.watch("indice");
  const maquetteId = form.watch("maquetteId");
  const isMaquette = kind === "maquette";
  const maquettes = items.filter((i) => i.kind === "maquette");

  const onSubmit = form.handleSubmit(async (data) => {
    setSubmitting(true);
    try {
      const linkedMaquette = isMaquette
        ? undefined
        : maquettes.find((m) => String(m.id) === data.maquetteId);
      const metadata = {
        authorContractId: Number(data.authorContractId),
        number: data.number,
        title: data.title,
        ...(data.indice ? { indice: data.indice } : {}),
        ...(isMaquette ? { isMaquette: true } : {}),
        ...(linkedMaquette ? { maquetteId: linkedMaquette.id } : {}),
      };
      const { data: created, error } = await apiClient.POST(
        "/api/yards/{yardId}/ftms/{ftmId}/documents",
        {
          params: {
            path: { yardId: String(yard.id), ftmId: String(ftm.id) },
          },
          body: {
            metadata: JSON.stringify(metadata),
            pdf: isMaquette ? undefined : (pdfFile ?? undefined),
            native: nativeFile ?? undefined,
            ifc: isMaquette ? (ifcFile ?? undefined) : undefined,
          },
          bodySerializer: multipartSerializer,
        },
      );
      if (error || isNil(created)) {
        toaster.error({
          title: "Erreur",
          description: error?.error ?? "Échec de l'enregistrement",
        });
        return;
      }
      setItems((prev) => [
        ...prev,
        {
          id: created.data.ftmDocumentId,
          kind: data.kind,
          number: data.number,
          title: data.title,
          indice: data.indice,
          maquetteNumber: linkedMaquette?.number ?? null,
        },
      ]);
      toaster.success({
        title: "Succès",
        description: isMaquette
          ? "Maquette enregistrée"
          : "Document enregistré",
      });
      form.reset(defaultDocumentValues);
      setPdfFile(null);
      setNativeFile(null);
      setIfcFile(null);
      setFormKey((k) => k + 1);
    } catch (err) {
      console.error("FTM document registration failed", err);
      toaster.error({
        title: "Erreur",
        description: "Une erreur est survenue",
      });
    } finally {
      setSubmitting(false);
    }
  });

  return (
    <form key={formKey} onSubmit={onSubmit} className="space-y-4">
      <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
        {`Documents et maquettes de la FTM n° ${ftm.number}`}
      </h3>

      {items.length > 0 && (
        <ul className="space-y-1 rounded-md bg-green-50 p-3 text-sm text-green-800 dark:bg-green-900/20 dark:text-green-300">
          {items.map((item) => (
            <li key={item.id}>
              {item.indice
                ? `${item.number} — ${item.title} (indice ${item.indice})`
                : `${item.number} — ${item.title}`}
              {item.kind === "maquette" && (
                <span className="ml-2 text-xs font-medium">Maquette</span>
              )}
              {isNotNil(item.maquetteNumber) && (
                <span className="ml-2 text-xs">
                  {`Maquette ${item.maquetteNumber}`}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      <fieldset>
        <legend className={labelClass}>Type</legend>
        <div className="flex gap-6 text-sm text-zinc-900 dark:text-zinc-100">
          <label className="flex items-center gap-2">
            <input type="radio" value="document" {...kindField} />
            Document
          </label>
          <label className="flex items-center gap-2">
            <input type="radio" value="maquette" {...kindField} />
            Maquette
          </label>
        </div>
      </fieldset>

      <div>
        <span className={labelClass}>
          Contrat auteur <span className="text-red-500">*</span>
        </span>
        <Select
          options={authorOptions}
          value={authorContractId ? [authorContractId] : []}
          onValueChange={(details) =>
            form.setValue("authorContractId", details.value[0] ?? "")
          }
          placeholder="Contrat auteur"
          required
        />
      </div>

      <div>
        <label htmlFor="ftmDocNumber" className={labelClass}>
          N° <span className="text-red-500">*</span>
        </label>
        <input
          id="ftmDocNumber"
          type="text"
          {...form.register("number")}
          required
          maxLength={255}
          className={inputClass}
        />
      </div>

      <div>
        <label htmlFor="ftmDocTitle" className={labelClass}>
          Intitulé <span className="text-red-500">*</span>
        </label>
        <input
          id="ftmDocTitle"
          type="text"
          {...form.register("title")}
          required
          maxLength={512}
          className={inputClass}
        />
      </div>

      <div>
        <span className={labelClass}>Indice</span>
        <Select
          options={DOCUMENT_INDICES.map((idx) => ({ label: idx, value: idx }))}
          value={indice ? [indice] : []}
          onValueChange={(details) =>
            form.setValue("indice", details.value[0] ?? "")
          }
          placeholder="Indice"
        />
      </div>

      {isMaquette ? (
        <div>
          <label htmlFor="ftmDocIfc" className={labelClass}>
            Fichier IFC
          </label>
          <input
            id="ftmDocIfc"
            type="file"
            accept={`.${IFC_EXTENSION}`}
            onChange={pickFile(
              setIfcFile,
              [IFC_EXTENSION],
              MAX_NATIVE_SIZE_BYTES,
              "200 Mo",
            )}
            className={fileInputClass}
          />
        </div>
      ) : (
        <>
          <div>
            <span className={labelClass}>Maquette associée</span>
            {maquettes.length > 0 ? (
              <Select
                options={[
                  { label: "Aucune", value: NO_MAQUETTE },
                  ...maquettes.map((m) => ({
                    label: `${m.number} — ${m.title}`,
                    value: String(m.id),
                  })),
                ]}
                value={[maquetteId]}
                onValueChange={(details) =>
                  form.setValue("maquetteId", details.value[0] ?? NO_MAQUETTE)
                }
                placeholder="Maquette associée"
              />
            ) : (
              <p className="text-sm text-zinc-500">
                Aucune maquette dans cette FTM. Enregistrez d'abord la maquette
                pour y rattacher ce document.
              </p>
            )}
          </div>

          <div>
            <label htmlFor="ftmDocPdf" className={labelClass}>
              Fichier PDF
            </label>
            <input
              id="ftmDocPdf"
              type="file"
              accept={ALLOWED_PDF_EXTENSIONS.map((e) => `.${e}`).join(",")}
              onChange={pickFile(
                setPdfFile,
                ALLOWED_PDF_EXTENSIONS,
                MAX_PDF_SIZE_BYTES,
                "50 Mo",
              )}
              className={fileInputClass}
            />
          </div>
        </>
      )}

      <div>
        <label htmlFor="ftmDocNative" className={labelClass}>
          Fichier natif
        </label>
        <input
          id="ftmDocNative"
          type="file"
          accept={ALLOWED_NATIVE_EXTENSIONS.map((e) => `.${e}`).join(",")}
          onChange={pickFile(
            setNativeFile,
            ALLOWED_NATIVE_EXTENSIONS,
            MAX_NATIVE_SIZE_BYTES,
            "200 Mo",
          )}
          className={fileInputClass}
        />
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" outline onClick={onDone} disabled={submitting}>
          Terminer
        </Button>
        <Button type="submit" disabled={submitting}>
          {submitting
            ? "En cours..."
            : isMaquette
              ? "Ajouter la maquette"
              : "Ajouter le document"}
        </Button>
      </div>
    </form>
  );
}

export type RegisterFtmPanelProps = {
  onClose: () => void;
};

/**
 * Consigner la réception d'une FTM (écran 002) : d'abord la FTM, puis ses
 * documents et maquettes un par un, chacun rattaché à la FTM créée.
 */
export function RegisterFtmPanel({ onClose }: RegisterFtmPanelProps) {
  const [ftm, setFtm] = useState<CreatedFtm | null>(null);

  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={onClose}
        className="text-sm text-blue-600 hover:underline dark:text-blue-400"
      >
        Retour aux actions
      </button>
      {isNil(ftm) ? (
        <FtmForm onCreated={setFtm} onCancel={onClose} />
      ) : (
        <FtmDocumentsForm ftm={ftm} onDone={onClose} />
      )}
    </div>
  );
}
